import React, { Component } from "react";
import { Col } from "react-grid-system";
import { IoTrashOutline } from "react-icons/io5";
import { LuMinus, LuPlus } from "react-icons/lu";
import NumberFormat from "react-number-format";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { Button, Dropdown, Form, Tab } from "semantic-ui-react";
import { ProductModel } from "../../entities/admin.product.model";
import { ClientModel } from "../../entities/client.model";
import store from "../../store";
import { generateReceiptHTML, generateReceiptHTMLA4, ReceiptData } from "../../utils/receiptGenerator";
import { getEffectivePrice } from "../../utils/shared.utils";
import StoreItem from "./store-item";
import "./store-products.scss";

interface ProductsProps {
  client: ClientModel;
  products: ProductModel[];
  basketItems: any[];
  totalAmount: number;
}

interface ProductsState {
  searchText: string;
  filteredProducts: ProductModel[];
  loading: boolean;
  successMsg: string;
  errorMsg: string;
  companyOptions: any[];
  companyMap: Map<number, any>;
  selectedCompanyId?: number;
  selectedCompany?: any;
  companyName?: string;
  companyRegister?: string;
  status: string;
  selectedPaymentMethod?: string;
  printed: boolean;
  orderToken: string;
  activeTab: 'individual' | 'company';
  showBasketUpdate: boolean;
}

class StoreProducts extends Component<ProductsProps, ProductsState> {

  private basketAnimationTimeout?: NodeJS.Timeout;
  private searchInputRef = React.createRef<HTMLInputElement>();

  paymentMethods = [
    { key: 0, value: "card", text: "Карт" },
    { key: 1, value: "qpay", text: "Qpay" },
    { key: 2, value: "account", text: "Данс" },
    { key: 3, value: "cash", text: "Бэлэн" },
    { key: 4, value: "loan", text: "Зээл" },
  ]

  constructor(props: any) {
    super(props);
    this.state = {
      searchText: "",
      loading: false,
      filteredProducts: [],
      errorMsg: "",
      successMsg: "",
      companyOptions: [],
      companyMap: new Map(),
      selectedCompanyId: undefined,
      selectedCompany: undefined,
      companyName: "",
      status: "new",
      companyRegister: "",
      selectedPaymentMethod: "",
      printed: false,
      orderToken: "",
      activeTab: 'individual',
      showBasketUpdate: false,
    };
  }

  componentDidMount() {
    this.fetchCompanies();
    // Auto-focus the search input on page load only
    requestAnimationFrame(() => {
      setTimeout(() => {
        if (this.searchInputRef.current) {
          this.searchInputRef.current.focus();
        }
      }, 300);
    });
  }

  componentWillReceiveProps(nextProps: any) {
    if (nextProps.products) {
      let products: ProductModel[] = [];
      for (let p of nextProps.products) {
        if (!p.active) continue;
        products.push(p);
      }
      products.sort((a: any, b: any) => {
        if (
          (a.warehouseCount === 0 && b.warehouseCount === 0) ||
          (a.warehouseCount > 0 && b.warehouseCount > 0)
        ) {
          // id desc
          return a.id < b.id ? 1 : -1;
        } else {
          // stocked first
          return a.warehouseCount === 0 ? 1 : -1;
        }
      });

      this.setState(
        {
          filteredProducts: products,
        }
      );
    }
  }

  filterProducts() {
    const search = this.state.searchText.trim().toLowerCase();
    if (!search) {
      this.setState({ filteredProducts: [] });
      return;
    }
    const filtered = this.props.products.filter((p: any) => {
      return (
        p.name?.toLowerCase().includes(search) ||
        p.barCode?.toLowerCase().includes(search)
      );
    });

    // If only one result and search exactly matches barcode, add to basket instead of showing
    if (filtered.length === 1) {
      const singleProduct = filtered[0];

      if (singleProduct.barCode && singleProduct.barCode === search) {
        // Add product to basket directly
        store.dispatch({
          type: "addToBasket",
          product: singleProduct,
        });
        this.setState({ searchText: "", filteredProducts: [] })
        this.showBasketUpdateAnimation();
        return;
      }
    }

    this.setState({ filteredProducts: filtered });
  }

  onInputChange(e: any) {
    this.setState({ searchText: e.target.value })
    setTimeout(() => {
      this.filterProducts();
    });
  };

  handleKeyDown = (e: any) => {
    if (e.key === "Enter") {
      this.filterProducts()
    }
  };

  removeItemFromBasket = (product: any) => {
    store.dispatch({
      type: "removeItemFromBasket",
      product: product,
    });
    this.showBasketUpdateAnimation();
  };

  onItemCountChange = (index: number, e: any) => {
    if (!e.target.value || e.target.value === "0") {
      store.dispatch({
        type: "updateBasketItemCount",
        product: this.props.basketItems[index],
        count: "0",
      });
      this.showBasketUpdateAnimation();
      return;
    }
    let count: number = Number(e.target.value);
    // if (count > this.props.basketItems[index].warehouseCount) {
    //   return;
    // }
    store.dispatch({
      type: "updateBasketItemCount",
      product: this.props.basketItems[index],
      count: count,
    });
    this.showBasketUpdateAnimation();
  };

  onItemCountBlur = (index: number, e: any) => {
    if (!e.target.value || e.target?.value === "0") {
      store.dispatch({
        type: "removeItemFromBasket",
        product: this.props.basketItems[index],
      });
    }
  };

  itemIncreaseCount = (index: number) => {
    if (this.state.status === "confirmed") {
      return;
    }
    let count = this.props.basketItems[index].count;
    // if (count >= this.props.basketItems[index].warehouseCount) {
    //   return;
    // }
    count += 1;
    store.dispatch({
      type: "updateBasketItemCount",
      product: this.props.basketItems[index],
      count: count,
    });
    this.showBasketUpdateAnimation();
  };

  itemDecreaseCount = (index: number) => {
    if (this.state.status === "confirmed") {
      return;
    }
    let count = this.props.basketItems[index].count;
    if (count < 1) {
      count = 0;
    } else {
      count -= 1;
    }
    store.dispatch({
      type: "updateBasketItemCount",
      product: this.props.basketItems[index],
      count: count,
    });
    this.showBasketUpdateAnimation();
  };

  onCompanyChange = (e: any, data: any) => {
    const selectedCompany = this.state.companyOptions.find((company: any) => company.value === data.value);
    if (selectedCompany) {
      const parts = selectedCompany.text.split(', ');
      this.setState({
        selectedCompany: selectedCompany,
        selectedCompanyId: data.value,
        companyName: parts[0] || "",
        companyRegister: parts[1] || "",
        // phone: parts[2] || ""
      });
    } else {
      this.setState({
        selectedCompany: undefined,
        selectedCompanyId: undefined,
        companyName: "",
        companyRegister: "",
        // phone: ""
      });
    }
  };

  onTabChange = (e: any, { activeIndex }: any) => {
    this.setState({
      activeTab: activeIndex === 0 ? 'individual' : 'company',
      selectedCompanyId: undefined,
      selectedCompany: undefined,
      companyName: "",
      companyRegister: ""
    });
  };

  // onPhoneChange = (e: any) => {
  //   this.setState({ phone: e.target.value })
  // }

  onCompanyNameChange = (e: any) => {
    this.setState({ companyName: e.target.value })
  }

  onCompanyRegisterChange = (e: any) => {
    this.setState({ companyRegister: e.target.value })
  }

  showBasketUpdateAnimation = () => {
    // Clear any existing timeout to prevent multiple animations
    if (this.basketAnimationTimeout) {
      clearTimeout(this.basketAnimationTimeout);
    }

    this.setState({ showBasketUpdate: true });

    this.basketAnimationTimeout = setTimeout(() => {
      this.setState({ showBasketUpdate: false });
    }, 1000);
  }

  onPaymentMethodChange = (e: any, data: any) => {
    this.setState({ selectedPaymentMethod: data.value });
  };

  fetchCompanies() {
    var token = localStorage.getItem('clientToken')
    if (token && token !== 'undefined') {
      fetch(`${process.env.REACT_APP_API_URL}client-list`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': token,
          'Content-Type': 'application/json'
        }
      }).then(res => {
        if (res.ok) {
          res.json().then((resJson: any) => {
            this.setState({ errorMsg: "" })
            let options: any[] = []
            let companyMap = new Map()
            for (let i = 0; i < resJson.length; i++) {
              let company = resJson[i]
              options.push({
                key: company.id,
                value: company.id,
                text: company.name + ", " + company.register
              })
              companyMap.set(company.id, company)
            }
            this.setState({ companyOptions: options, companyMap })
          })
        }
      }).catch(err => {
        this.setState({ errorMsg: "Сүлжээний алдаа" })
        console.error(err);
      })
    } else {
      console.debug("Will redirect to: " + window.location.pathname)
      store.dispatch({ type: "redirectUrl", data: window.location.pathname })
    }
  }

  validate() {
    if (this.props.basketItems.length === 0) {
      this.setState({ errorMsg: "Бүтээгдэхүүн сонгоно уу" })
      return false;
    }

    if (this.state.activeTab === 'individual') {
      // if (!this.validatePhone()) {
      //   return false;
      // }
    }

    // If company tab is active and no company selected, validate manual company fields
    if (this.state.activeTab === 'company' && !this.state.selectedCompanyId) {
      if (!this.state.companyName || this.state.companyName.trim() === '') {
        this.setState({ errorMsg: "Байгууллагын нэр оруулна уу" })
        return false;
      }
      if (!this.state.companyRegister || this.state.companyRegister.trim() === '') {
        this.setState({ errorMsg: "Байгууллагын регистр оруулна уу" })
        return false;
      }
      // if (!this.validatePhone()) {
      //   return false;
      // }
    }
    if (!this.state.selectedPaymentMethod) {
      this.setState({ errorMsg: "Төлбөр хэрэгсэл сонгоно уу" })
      return false;
    }
    return true;
  }

  // validatePhone() {
  //   if (!this.state.phone) {
  //     this.setState({ errorMsg: "Утасны дугаар оруулна уу" })
  //     return false;
  //   }
  //   //validate phone number: only numbers:
  //   var re = /^(\+{0,})(\d{0,})([(]{1}\d{1,3}[)]{0,}){0,}(\s?\d+|\+\d{2,3}\s{1}\d+|\d+){1}[\s|-]?\d+([\s|-]?\d+){1,2}(\s){0,}$/;
  //   if (!re.test(String(this.state.phone).toLowerCase())) {
  //     this.setState({ errorMsg: "Утасны дугаар буруу форматтай байна" })
  //     return false;
  //   }
  //   return true;
  // }

  preparePayload() {
    const isClubMember = this.state.companyMap.get(this.state.selectedCompanyId!)?.clubMember;
    const items: any[] = [];
    for (let basketItem of this.props.basketItems) {
      if (basketItem.count) {
        let item = {
          productId: basketItem.id,
          count: basketItem.count,
          price: getEffectivePrice(basketItem, isClubMember),
          customPrice: 0
        }
        items.push(item)
      }
    }

    return {
      companyId: this.state.selectedCompanyId || null,
      // phone: this.state.selectedCompanyId ? null : this.state.phone,
      companyName: this.state.companyName,
      companyRegister: this.state.companyRegister,
      products: items,
      method: this.state.selectedPaymentMethod,
    };
  }

  submitOrder() {
    // Prevent duplicate submissions - check status before proceeding
    if (this.state.status === "confirmed" || this.state.loading) {
      return;
    }

    var token = localStorage.getItem('clientToken')
    if (token && token !== 'undefined') {
      this.setState({ errorMsg: "", loading: true })
      let payload = this.preparePayload();
      const apiUrl = `${process.env.REACT_APP_API_URL}client/order-store`;

      fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Authorization': token,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      }).then(res => {
        if (res.ok) {
          res.json().then((resJson: any) => {
            this.setState({
              status: "confirmed",
              orderToken: resJson.token,
              successMsg: "Захиалгын дугаар: " + resJson.token,
              errorMsg: "",
              loading: false
            })
          }).catch((jsonErr: any) => {
            // Handle JSON parsing errors
            this.setState({ errorMsg: "Захиалга үүсгэхэд алдаа гарлаа " + jsonErr?.message, loading: false })
            console.error("JSON parsing error:", jsonErr);
          })
        }
        else if (res.status === 409) {
          res.json().then((resJson: any) => {
            if (resJson.errors && resJson.errors.length) {
              let errorMsg = "Үлдэгдэл хүрэлцэхгүй бүтээгдэхүүн: "
              for (let stockErr of resJson.errors) {
                errorMsg += `${stockErr.name}, `
              }
              errorMsg = errorMsg.substring(0, errorMsg.length - 2)
              this.setState({ errorMsg, loading: false })
            }
            else if (resJson.products && resJson.products.length) {
              let errorMsg = []
              for (let priceError of resJson.products) {
                errorMsg.push(priceError.name + ": " + priceError.price)
              }
              errorMsg.push("Үнийн зөрүү үүссэн бол барааг устгаад, вебээ рефреш хийнэ үү")
              this.setState({ errorMsg: (resJson.error || "Үнийн зөрүү") + ". " + errorMsg.join(", "), loading: false })
            }
            else if (resJson.error) {
              this.setState({ errorMsg: resJson.error, loading: false })
            }
            else {
              this.setState({ errorMsg: "Захиалгыг баталгаажуулах боломжгүй", loading: false })
            }
          }).catch((jsonErr: any) => {
            this.setState({ errorMsg: "409 хариутай холбоотой алдаа гарлаа", loading: false })
            console.error("JSON parsing error:", jsonErr);
          })
        }
        else {
          res.json().then((resJson: any) => {
            if (resJson && resJson.error) {
              this.setState({ errorMsg: resJson.error, loading: false });
            }
            else {
              this.setState({ errorMsg: "Серверт алдаа гарлаа ", loading: false })
            }
          }).catch((jsonErr: any) => {
            // Handle JSON parsing errors
            this.setState({ errorMsg: "Алдааг боловсруулж чадсангүй", loading: false })
            console.error("JSON parsing error:", jsonErr);
          })
        }
      }).catch(err => {
        this.setState({ errorMsg: "Сүлжээний алдаа", loading: false })
        console.error(err);
      })
    }
    else {
      console.debug("Will redirect to: " + window.location.pathname)
      store.dispatch({ type: "redirectUrl", data: window.location.pathname })
    }
  }

  handleSubmit = () => {
    // Prevent duplicate submissions
    if (this.state.status === "confirmed" || this.state.loading) {
      return;
    }
    if (!this.validate()) {
      return;
    }
    this.submitOrder();
  };

  reset = () => {
    store.dispatch({
      type: 'clearBasket'
    })
    this.setState({
      status: "new",
      successMsg: "",
      errorMsg: "",
      orderToken: "",
      printed: false,
      selectedPaymentMethod: "",
      selectedCompanyId: undefined,
      selectedCompany: undefined,
      companyName: "",
      companyRegister: "",
      // phone: "",
      activeTab: 'individual'
    })
  }

  printReceipt = (a4: boolean) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const receiptData: ReceiptData = {
      businessName: "Шеф Хоул Сайл",
      taxId: "123123",
      employeeName: this.props.client.ownName,
      paymentMethod: this.paymentMethods.find(p => p.value === this.state.selectedPaymentMethod)?.text || '',
      companyRegister: this.state.selectedCompanyId ? this.state.companyRegister : "",
      // phone: this.state.selectedCompanyId ? "" : this.state.phone,
      orderToken: this.state.orderToken,
      items: this.props.basketItems.map(item => ({
        name: item.name,
        count: item.count,
        price: getEffectivePrice(item, this.state.companyMap.get(this.state.selectedCompanyId!)?.clubMember),
        saleActive: false,
        salePrice: 0
      }))
    };

    const receipt = a4 ? generateReceiptHTMLA4(receiptData) : generateReceiptHTML(receiptData);
    printWindow.document.write(receipt);
    printWindow.focus();
    printWindow.print();

    // Clear basket after printing
    store.dispatch({ type: 'REFRESH_PRODUCTS' })
    this.setState({ printed: true })
  }

  render() {
    if (this.props.client.superUser !== 1) {
      return (
        <section className="store-products-container bordered" id='products-content'>
          <div className='products-content'>
            <div className="full-width text-center error-message">
              <h2>Хандах эрх байхгүй</h2>
              <p>Та энэ хуудсанд хандах эрхгүй байна.</p>
            </div>
          </div>
        </section>
      );
    }

    return (
      <section
        className={
          "store-products-container bordered"
        }
        id='products-content'>

        <div className='products-content'>
          {/* <div className="full-width text-center">
              <h1>Хайлт</h1>
            </div> */}

          {/* Basket Update Animation */}
          {this.state.showBasketUpdate && (
            <div className="basket-update-notification">
              Сагс шинэчлэгдлээ
            </div>
          )}

          <div className='search-input'>
            <input
              ref={this.searchInputRef}
              type='text'
              autoComplete='off'
              autoCapitalize="none"
              autoFocus
              value={this.state.searchText}
              onChange={(e) => this.onInputChange(e)}
              onKeyDown={this.handleKeyDown}
              placeholder='Баркод, нэр ...'
              className='input'
            />
            <Button className='icon icon-search cursor search-button' />
          </div>

          <div className='products-body'>
            {(this.state.searchText) && this.state.filteredProducts &&
              this.state.filteredProducts.length > 0 ? (
              this.state.filteredProducts.map((product: any) => {
                return (
                  <Col
                    className='product-item-col'
                    xs={3}
                    sm={4}
                    md={2}
                    lg={2}
                    key={`product-${product.id}`}>
                    <StoreItem product={product} />
                  </Col>
                );
              })
            ) : (this.state.searchText) ? (
              <div className='no-product'>Бүтээгдэхүүн олдсонгүй</div>
            ) : (
              <div className='no-product'>Хайлт хийж эхлэнэ үү</div>
            )}
          </div>

          {this.props.basketItems && this.props.basketItems.length > 0 && (
            <div className="basket">
              <div className="basket-header">
                <h3>Сагс ({this.props.basketItems.length})</h3>
              </div>
              <div className="basket-items">
                {this.props.basketItems.map((product: any, pindex: number) => {
                  return (
                    <div className="basket-item" key={"basket-item-" + pindex}>
                      <div className="product-details">
                        <div className="product-row-1">
                          <Link to={"/product/" + product.slug}>
                            <img
                              className="product-image"
                              src={product.imageSmUrl}
                            />
                          </Link>
                          <div className="product-name">
                            <div className="name">
                              <Link to={"/product/" + product.slug}>
                                {product.name}
                                {product.size && product.unit ? (
                                  <span className="price-per">
                                    ({product.size + " " + product.unit})
                                  </span>
                                ) : null}
                              </Link>
                            </div>
                          </div>
                          <span
                            className={"remove" + (this.state.status === "confirmed" ? " hidden" : "")}
                            onClick={() => this.removeItemFromBasket(product)}
                          >
                            <IoTrashOutline size={20} />
                          </span>
                        </div>
                        <div className="product-row-2">
                          <div className="product-amount">
                            <div className="product-count">
                              <LuMinus
                                className="caret"
                                onClick={() => this.itemDecreaseCount(pindex)}
                              />
                              <input
                                type="text"
                                value={product.count}
                                inputMode="numeric"
                                disabled={this.state.status === "confirmed"}
                                onChange={(e) => this.onItemCountChange(pindex, e)}
                                onBlur={(e) => this.onItemCountBlur(pindex, e)}
                              />
                              <LuPlus
                                className="caret"
                                onClick={() => this.itemIncreaseCount(pindex)}
                              />
                            </div>
                            <div className="stock-line">
                              {product.warehouseCount <= product.count
                                ? "Боломжит үлдэгдэл: " + product.warehouseCount
                                : ""}
                            </div>
                          </div>
                          <div className="product-price">
                            <NumberFormat
                              displayType={"text"}
                              value={getEffectivePrice(product, this.state.companyMap.get(this.state.selectedCompanyId!)?.clubMember)}
                              suffix=""
                              thousandSeparator={true}
                            />
                            &nbsp;x <span>{product.count}</span> =
                            <NumberFormat
                              displayType={"text"}
                              value={getEffectivePrice(product, this.state.companyMap.get(this.state.selectedCompanyId!)?.clubMember) * product.count}
                              suffix=""
                              thousandSeparator={true}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="basket-total">
                <div className="total-row">
                  <span className="grand-total">Нийт дүн:</span>
                  <NumberFormat
                    displayType={"text"}
                    value={this.props.basketItems.reduce((sum, p) =>
                      sum + getEffectivePrice(p, this.state.companyMap.get(this.state.selectedCompanyId!)?.clubMember) * p.count, 0
                    )}
                    suffix="₮"
                    thousandSeparator={true}
                  />
                </div>
              </div>
            </div>
          )}

          <div className="full-width">
            <Tab
              panes={[
                {
                  menuItem: 'Хувь хүн',
                  render: () => (
                    <Tab.Pane>
                      <Form style={{ marginTop: '20px', marginBottom: '20px' }}>
                        {/* <Form.Group style={{ marginBottom: '20px' }}>
                          <Form.Field width={16}>
                            <Form.Input
                              required
                              disabled={this.state.status === 'confirmed'}
                              label="Утасны дугаар"
                              placeholder="Утасны дугаар"
                              value={this.state.phone}
                              onChange={this.onPhoneChange}
                              style={{ fontSize: 16 }}
                            />
                          </Form.Field>
                        </Form.Group> */}
                      </Form>
                    </Tab.Pane>
                  )
                },
                {
                  menuItem: 'Байгууллага',
                  render: () => (
                    <Tab.Pane>
                      <Form style={{ marginTop: '20px', marginBottom: '20px' }}>
                        <Form.Group>
                          <Form.Field width={16}>
                            <label>Байгууллага сонгох</label>
                          </Form.Field>
                        </Form.Group>
                        <Form.Group style={{ marginBottom: '20px' }}>
                          <Form.Field width={16}>
                            <Dropdown
                              selection
                              disabled={this.state.status === 'confirmed'}
                              options={this.state.companyOptions}
                              placeholder="байгууллага сонгоогүй"
                              value={this.state.selectedCompanyId}
                              style={{ fontSize: 16 }}
                              search
                              clearable
                              onChange={this.onCompanyChange} />
                          </Form.Field>
                        </Form.Group>

                        {!this.state.selectedCompanyId ? (
                          <>
                            <Form.Field width={16}>
                              <Form.Input
                                required
                                disabled={this.state.status === 'confirmed'}
                                label="Байгууллагын нэр"
                                placeholder="Байгууллагын нэр оруулах"
                                value={this.state.companyName}
                                onChange={this.onCompanyNameChange} />
                            </Form.Field>
                            <Form.Field width={16}>
                              <Form.Input
                                required
                                disabled={this.state.status === 'confirmed'}
                                label="Байгууллагын регистр"
                                placeholder="Байгууллагын регистр оруулах"
                                value={this.state.companyRegister}
                                onChange={this.onCompanyRegisterChange} />
                            </Form.Field>
                            {/* <Form.Field width={16}>
                              <Form.Input
                                required
                                disabled={this.state.status === 'confirmed'}
                                label="Утасны дугаар"
                                placeholder="Утасны дугаар оруулах"
                                value={this.state.phone}
                                onChange={this.onPhoneChange} />
                            </Form.Field> */}
                          </>
                        ) : null}
                      </Form>
                    </Tab.Pane>
                  )
                }
              ]}
              onTabChange={this.onTabChange}
            />
          </div>

          <Form style={{ marginTop: '20px', marginBottom: '20px' }}>
            <Form.Group style={{ marginBottom: '20px' }}>
              <Form.Field width={16}>
                <Dropdown
                  selection
                  required
                  disabled={this.state.status === 'confirmed'}
                  options={this.paymentMethods}
                  placeholder="Төлбөр хэрэгсэл сонгоогүй"
                  value={this.state.selectedPaymentMethod}
                  style={{ fontSize: 16 }}
                  onChange={this.onPaymentMethodChange} />
              </Form.Field>
            </Form.Group>

            <div className="error-msg">{this.state.errorMsg}</div>
            <div className="success-msg">{this.state.successMsg}</div>

            <Form.Group style={{ marginBottom: '20px' }}>
              <Form.Field width={16}>
                <Button 
                  loading={this.state.loading}
                  disabled={this.state.loading || this.state.status === "confirmed"} 
                  fluid 
                  onClick={this.handleSubmit}
                >
                  БАТЛАХ
                </Button>
              </Form.Field>
            </Form.Group>

            {this.state.status === "confirmed" && (
              <>
                <Form.Group style={{ marginBottom: '20px' }}>
                  <Form.Field width={16}>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <Button fluid onClick={() => this.printReceipt(false)}>БАРИМТ 58mm</Button>
                      <Button fluid onClick={() => this.printReceipt(true)}>БАРИМТ A4</Button>
                    </div>
                  </Form.Field>
                </Form.Group>
              </>
            )}

            {this.state.printed && (
              <Form.Group style={{ marginTop: '20px', marginBottom: '20px' }}>
                <Form.Field width={16}>
                  <Button primary fluid onClick={this.reset}>ШИНЭ ЗАХИАЛГА</Button>
                </Form.Field>
              </Form.Group>
            )}
          </Form>
        </div>
      </section>
    );
  }
}

const mapStateToProps = (state: any) => {
  let basketItems: any[] = [];
  let totalAmount: number = 0;

  if (state.products && state.basket && state.basket.quantityById) {
    state.products.forEach((product: any) => {
      if (
        state.basket.quantityById[product.id] !== null &&
        state.basket.quantityById[product.id] !== undefined
      ) {
        // exists in the basket
        product["count"] = state.basket.quantityById[product.id];
        // Add timestamp for sorting (fallback to 0 for existing items without timestamps)
        product["addedTimestamp"] = state.basket.addedTimestamps && state.basket.addedTimestamps[product.id]
          ? state.basket.addedTimestamps[product.id]
          : 0;
        basketItems.push(product);
        totalAmount +=
          product.count *
          (product.saleActive ? product.salePrice : product.price);
      }
    });

    // Sort basket items by timestamp (latest first)
    basketItems.sort((a, b) => b.addedTimestamp - a.addedTimestamp);
  }
  return {
    basketItems: basketItems,
    totalAmount: totalAmount,
    client: state.client,
  };
};

export default connect(mapStateToProps)(StoreProducts);
